import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { toast } from 'react-toastify';
import PermissionRequired from '../../errorPages/permissionRequired';

import { Box, Button, Container, Paper, TextField, Typography, Divider, Chip } from '@mui/material';

import { PATH_DASHBOARD } from '../../../routes/paths';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import Page from '../../../components/Page';
import useSettings from '../../../hooks/useSettings';
import { accessVerify, capitalize, handleNumber, safeJSONParse, utcMoment } from '../../../utils/common';
import { fCurrency } from '../../../utils/formatNumber';

import { dispatch, useSelector } from '../../../redux/store';
import { getDailySalesTargets, addOrUpdateDailySalesTargets } from '../../../redux/slices/saleRedux/salesRedux';

// Helpers
const toDateKey = (date) => moment(date).format('YYYY-MM-DD');

const normalizeBackendData = (data = {}) => Object.fromEntries(Object.entries(data).map(([rawDate, amount]) => [moment(rawDate).format('YYYY-MM-DD'), amount]));

const DailySalesTargetView = () => {
  const { themeStretch } = useSettings();

  // Local State
  const [selectedDates, setSelectedDates] = useState([]);
  const [targetAmount, setTargetAmount] = useState('');
  const [calendarDate, setCalendarDate] = useState(moment());

  // Redux State
  const { selectedLocation } = useSelector((state) => state.location);
  const { dailySalesTargets, addDailySalesTargetsData } = useSelector((state) => state.sales);

  // Memoized normalized daily targets
  const dailyTargets = useMemo(() => normalizeBackendData(dailySalesTargets?.data), [dailySalesTargets]);
  const canUpdateDailyTargets = accessVerify('UPDATE_DAILY_SALES_TARGETS');
  

  // Fetch daily targets when location or month changes or after adding/updating targets
  useEffect(() => {
    if (!selectedLocation?.id) return;
    setSelectedDates([]);
    setTargetAmount('');
    dispatch(getDailySalesTargets(calendarDate.year(), calendarDate.month() + 1, selectedLocation.id));
  }, [selectedLocation?.id, calendarDate, addDailySalesTargetsData?.data]);

  // Handle date selection in calendar
  const handleDateClick = ({ date }) => {
    if (!canUpdateDailyTargets) {
      return toast.error('You do not have permission to update daily sales targets.');
    }
    if (selectedLocation?.id == 1) return toast.error('Global Location can not have sales targets!');

    const dateKey = toDateKey(date);

    setSelectedDates((prev) => {
      const newSelected = prev.includes(dateKey) ? prev.filter((d) => d !== dateKey) : [...prev, dateKey].sort();

      // Auto-fill amount if first selection
      if (prev.length === 0 && dailyTargets[dateKey]) {
        setTargetAmount(dailyTargets[dateKey]);
      }

      return newSelected;
    });
  };

  const handleRemoveDate = (dateKey) => {
    setSelectedDates((prev) => prev.filter((d) => d !== dateKey));
  };

  const handleSubmit = () => {
    if (!selectedLocation?.id) return toast.error('Select location!');
    if (!selectedDates.length) return toast.error('Select at least one date !');
    const amount = Number(targetAmount);
    if (!amount || amount <= 0) return toast.error('Invalid amount !');

    dispatch(
      addOrUpdateDailySalesTargets({
        locationId: selectedLocation.id,
        dates: selectedDates,
        target: amount,
      })
    );

    setSelectedDates([]);
    setTargetAmount('');
  };

  // Memoized calendar events
  const calendarEvents = useMemo(() => {
    const savedEvents = Object.entries(dailyTargets)
      .map(([date, value]) => {
        if (typeof value === 'number') {
          // Single location
          return {
            id: `saved-${date}`,
            start: date,
            allDay: true,
            extendedProps: { type: 'saved', amount: value },
          };
        } else if (value?.total !== undefined) {
          // Global: aggregated total + breakdown
          return {
            id: `saved-${date}`,
            start: date,
            allDay: true,
            extendedProps: {
              type: 'global',
              total: value.total,
              breakdown: value.breakdown,
            },
          };
        }
        return null;
      })
      .filter(Boolean);

    const selectedEvents = selectedDates.map((date) => ({
      id: `selected-${date}`,
      start: date,
      allDay: true,
      display: 'background',
      classNames: ['day-selected'],
      extendedProps: { type: 'selected' },
    }));

    return [...savedEvents, ...selectedEvents];
  }, [dailyTargets, selectedDates]);

  // Event rendering
  const renderEventContent = (eventInfo) => {
    const { type, amount, total, breakdown } = eventInfo.event.extendedProps;

    if (type === 'saved') {
      return <Box sx={{ backgroundColor: 'primary.main', fontSize: 12, fontWeight: 600, textAlign: 'center' }}>{fCurrency(amount)}</Box>;
    }

    if (type === 'global') {
      return (
        <Box
          sx={{
            backgroundColor: 'primary.main',
            fontSize: 12,
            fontWeight: 600,
            textAlign: 'center',
          }}
          title={breakdown.map((b) => `${b.locationName}: ${fCurrency(b.amount)}`).join('\n')}
        >
          {fCurrency(total)}
        </Box>
      );
    }

    return null;
  };

  return (
    <Page title='Daily Sales Targets'>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs heading='Daily Sales Targets' links={[{ name: 'Dashboard', href: PATH_DASHBOARD.root }, { name: 'Sale' }, { name: 'Daily Targets' }]} />

        {accessVerify('DAILY_SALES_TARGETS_PAGE') ? (
          <Box display='flex' gap={3} mt={3} flexDirection={{ xs: 'column', md: 'row' }}>
            {/* Calendar */}
            <Paper sx={{ flex: 1 }}>
              <Box
                sx={{
                  '& .fc-daygrid-day': { cursor: 'pointer' },
                  '& .fc-daygrid-day.day-selected': {
                    border: '2px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1,
                  },
                  '& .fc-day-today': {
                    backgroundColor: 'warning.lighter !important',
                  },
                }}
              >
                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView='dayGridMonth'
                  dateClick={canUpdateDailyTargets ? handleDateClick : undefined}
                  events={calendarEvents}
                  eventContent={renderEventContent}
                  headerToolbar={{
                    left: 'prev,next today',
                    center: 'title',
                    right: 'monthlyTotal',
                  }}
                  customButtons={{
                    monthlyTotal: {
                      text: `${fCurrency(dailySalesTargets?.totalTargetPerMonth || 0)}`,
                      click: () => {}, // no action
                    },
                  }}
                  height='auto'
                  datesSet={(info) => {
                    // Use currentStart of the view → first day of displayed month
                    setCalendarDate(moment(info.view.currentStart));
                  }}
                />
              </Box>
            </Paper>

            {/* Side Panel */}
            <Paper sx={{ p: 3, flex: 1, maxWidth: { md: 420 } }}>
              <Typography variant='subtitle1' mb={2}>
                Selected Dates
              </Typography>

              {!selectedDates.length ? (
                <Typography color='text.secondary'>Select dates from the calendar</Typography>
              ) : (
                <>
                  <Box display='flex' flexWrap='wrap' gap={1} mb={2}>
                    {selectedDates.map((date) => (
                      <Chip key={date} label={moment(date).format('DD MMM')} onDelete={() => handleRemoveDate(date)} color='primary' variant='outlined' />
                    ))}
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {canUpdateDailyTargets ? (
                    <>
                      <TextField fullWidth type='number' label='Target Amount' value={targetAmount} onChange={(e) => setTargetAmount(e.target.value)} />

                      <Button sx={{ mt: 2 }} fullWidth variant='contained' onClick={handleSubmit} disabled={!targetAmount || !selectedDates.length}>
                        Save Target
                      </Button>
                    </>
                  ) : (
                    <Typography color='text.secondary'>You have view-only access.</Typography>
                  )}
                </>
              )}
            </Paper>
          </Box>
        ) : (
          <PermissionRequired />
        )}
      </Container>
    </Page>
  );
};

export default DailySalesTargetView;
